import { useEffect, useState, type ReactNode } from "react";
import { useDrop } from "react-dnd";
import cn from "classnames";
import { PackContentListItem } from "../../cards";
import type DragNDropItemType from "../../../enums/dragNDropItemTypes";
import { type DraggableAsset, type DraggableDroppedAsset, type DroppedItem } from "../../../interfaces/assetToDropInfo";
import EventTypes from "../../../enums/eventTypes";
import { getAssets, mapToDragItem } from "./utils";

import "./dropItemsList.scss";
import AssetTypes from "../../../enums/assetTypes";
import { ContentTypesEnum } from "enums";
import { FaIcons, Icons, RenderIcons } from "components/dropdowns/contentSegmentationDropdown/constants";
import { Icon, type SemanticICONS } from "semantic-ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export interface Props {
  acceptType: DragNDropItemType;
  items: DroppedItem[];
  displayItemsCount: number;
  onDropFailed: (message?: string) => void;
  addItems?: (assets: DroppedItem[]) => void;
  removeItem?: (id: number, type?: string) => void;
  isReadonly?: boolean;
  isDraggingDisabled?: boolean;
  noResults?: ReactNode;
  selectedCards: DraggableAsset[];
  resetSelectedCards: () => void;
}

const addIndexes = (items: DroppedItem[]) => {
  return items.map((asset, index) => {
    return { ...asset, index };
  });
};

const NAME_TRANSLATE: Record<string, ContentTypesEnum> = {
  // Assets
  Video: ContentTypesEnum.Videos,
  EmbeddedVideo: ContentTypesEnum["Embedded Videos"],
  Assessment: ContentTypesEnum.Assessments,
  Survey: ContentTypesEnum.Surveys,
  Pdf: ContentTypesEnum.PDFs,
  Link: ContentTypesEnum.Links,
  SCORM: ContentTypesEnum.Scorm,

  // Communications
  Email: ContentTypesEnum.Emails,
  Message: ContentTypesEnum.Messages,

  // Events
  ExternalEvent: ContentTypesEnum.Events,

  // Flows
  Flow: ContentTypesEnum.Flows,

  // Campaigns (TD add-on)
  Campaign: ContentTypesEnum.Campaigns,
};

function getIcon(type?: string): ReactNode {
  if (type && NAME_TRANSLATE[type]) {
    if (Icons[NAME_TRANSLATE[type]]) {
      return Icons[NAME_TRANSLATE[type]];
    } else if (FaIcons[NAME_TRANSLATE[type]]) {
      return <FontAwesomeIcon icon={FaIcons[NAME_TRANSLATE[type]]!} className="icon" />;
    } else if (RenderIcons[NAME_TRANSLATE[type]]) {
      return RenderIcons[NAME_TRANSLATE[type]]!();
    }
  }
  return "fa-file";
}

function createDescription(type?: string) {
  switch (type) {
    case EventTypes.ExternalEvent:
      return "Event";
    case AssetTypes.Pdf:
      return "PDF";
    case AssetTypes.EmbeddedVideo:
      return "Embedded Video";
    default:
      return type;
  }
}

const getMeta = (type?: string) => {
  if (!type) {
    return null;
  }
  const iconName = getIcon(type);
  if (typeof iconName === "string") {
    return (
      <>
        <Icon name={iconName as SemanticICONS} /> {createDescription(type)}
      </>
    );
  }
  return (
    <>
      {iconName} {createDescription(type)}
    </>
  );
};

export default function DropItemsList(props: Props) {
  const { items, removeItem, isReadonly, acceptType, displayItemsCount, noResults, selectedCards, resetSelectedCards } =
    props;
  const [cards, setCards] = useState(addIndexes(items));

  /* istanbul ignore next */
  const [{ isOver }, drop] = useDrop<DraggableDroppedAsset, any, any>(() => {
    return {
      accept: acceptType,
      drop: (item: DraggableDroppedAsset) => {
        if (item.index === undefined) {
          let draggedItems: DraggableAsset[] = selectedCards.length
            ? selectedCards.map(mapToDragItem)
            : [mapToDragItem(item)];

          const haveDuplicatedItems = draggedItems.some((asset) =>
            cards.some((c) => c.id === asset.id && c.type === asset.type),
          );

          const [assets, message] = getAssets(haveDuplicatedItems, draggedItems, cards);

          if (haveDuplicatedItems) {
            props.onDropFailed(message);
          }
          if (assets.length) {
            props.addItems?.(assets);
            resetSelectedCards();
            // blur
            (document.activeElement as HTMLElement)?.blur();
          }
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    };
  }, [props.selectedCards, cards]);

  useEffect(() => {
    setCards(addIndexes(items));
  }, [items]);

  if (!items.length) {
    return (
      <div ref={drop} className={cn("drop-items-list", { "hover-over": isOver })}>
        {noResults ?? null}
      </div>
    );
  }

  return (
    <div ref={drop} className={cn("drop-items-list", { "hover-over": isOver })}>
      {cards.slice(0, displayItemsCount).map((item: DroppedItem) => {
        return (
          <PackContentListItem
            key={item.id.toString() + (item.type ?? "")}
            id={item.id}
            title={item.title}
            type={item.type}
            thumbnailUrl={item.thumbnailUrl}
            meta={getMeta(item.type)}
            publisher={item.publisher}
            hidden={item.visibility === false}
            isReadOnly={isReadonly}
            onRemoveItemClick={removeItem}
          />
        );
      })}
    </div>
  );
}
